/**
 * MedCover Pro — Secure API Client
 * Auto token refresh, offline detection, encrypted storage
 */

import { toast } from '../components/UI';

/**
 * API base URL
 * - Development: relative "/api" (Vite dev server proxies to localhost:3001)
 * - Production (Vercel): VITE_API_URL = "https://api.thmedcoverpro.com/api"
 *
 * Configurar en Vercel: Settings → Environment Variables → VITE_API_URL
 * O en frontend/.env.production (commiteado, publico).
 */
const API_BASE = import.meta.env.VITE_API_URL || '/api';

// ═══════════════════════════════════════════════
// SECURE LOCAL STORAGE (obfuscated keys, no plaintext tokens)
// ═══════════════════════════════════════════════
const STORAGE_PREFIX = '_mc_';

function secureSet(key, value) {
  try {
    // Simple obfuscation to prevent casual inspection (not true encryption - that's server side)
    const encoded = btoa(encodeURIComponent(JSON.stringify(value)));
    localStorage.setItem(STORAGE_PREFIX + key, encoded);
  } catch (e) {
    console.error('Storage write failed');
  }
}

function secureGet(key) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(atob(raw)));
  } catch (e) {
    localStorage.removeItem(STORAGE_PREFIX + key);
    return null;
  }
}

function secureRemove(key) {
  localStorage.removeItem(STORAGE_PREFIX + key);
}

function clearAllSecure() {
  Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX)).forEach(k => localStorage.removeItem(k));
}

// ═══════════════════════════════════════════════
// OFFLINE DETECTION
// ═══════════════════════════════════════════════
let isOnline = navigator.onLine;
const onlineListeners = new Set();

window.addEventListener('online', () => {
  isOnline = true;
  onlineListeners.forEach(fn => fn(true));
  // Trigger service worker sync
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage('SYNC_NOW');
  }
});

window.addEventListener('offline', () => {
  isOnline = false;
  onlineListeners.forEach(fn => fn(false));
});

export function onConnectivityChange(fn) {
  onlineListeners.add(fn);
  return () => onlineListeners.delete(fn);
}

export function getOnlineStatus() {
  return isOnline;
}

// ═══════════════════════════════════════════════
// API CLIENT
// ═══════════════════════════════════════════════
class ApiClient {
  constructor() {
    this.token = secureGet('token');
    this.refreshToken = secureGet('refresh');
    this.refreshPromise = null;
    this.baseURL = API_BASE;
  }

  setTokens(token, refreshToken) {
    this.token = token;
    this.refreshToken = refreshToken;
    if (token) {
      secureSet('token', token);
      secureSet('refresh', refreshToken);
    } else {
      secureRemove('token');
      secureRemove('refresh');
    }
  }

  // ─── Suplantación (SuperAdmin "Ver como") ───
  // Se guarda la sesión del SA aparte y se usa el token del usuario suplantado
  // (1h, sin refresh). Al salir, o al caducar, se restaura la sesión del SA.
  isImpersonating() { return !!secureGet('sa_token'); }

  impersonationInfo() { return secureGet('imp_info') || null; }

  async startImpersonation(target) {
    const data = await this.post('/superadmin/impersonate', { user_id: target.id });
    if (!this.isImpersonating()) {
      secureSet('sa_token', this.token);
      if (this.refreshToken) secureSet('sa_refresh', this.refreshToken);
    }
    secureSet('imp_info', { ...data.user, tenant_name: target.tenant_name || null, expires_at: Date.now() + data.expires_in * 1000 });
    this.token = data.token;
    this.refreshToken = null;
    secureSet('token', data.token);
    secureRemove('refresh');
    this.invalidateCache();
    return data.user;
  }

  stopImpersonation() {
    const saToken = secureGet('sa_token');
    const saRefresh = secureGet('sa_refresh');
    secureRemove('sa_token'); secureRemove('sa_refresh'); secureRemove('imp_info');
    this.token = saToken;
    this.refreshToken = saRefresh;
    if (saToken) secureSet('token', saToken); else secureRemove('token');
    if (saRefresh) secureSet('refresh', saRefresh); else secureRemove('refresh');
    this.invalidateCache();
  }

  clearAuth() {
    // Server-side logout (blacklist tokens) — fire and forget
    if (this.token) {
      this.post('/auth/logout', { refreshToken: this.refreshToken }).catch(() => {});
    }
    this.token = null;
    this.refreshToken = null;
    clearAllSecure();
  }

  // ─── In-memory cache with TTL ───
  _cache = new Map();
  _cacheTTL = 30000; // 30 seconds default

  _getCached(key) {
    const entry = this._cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.time > (entry.ttl || this._cacheTTL)) { this._cache.delete(key); return null; }
    return entry.data;
  }

  _setCache(key, data, ttl) {
    this._cache.set(key, { data, time: Date.now(), ttl: ttl || this._cacheTTL });
  }

  invalidateCache(prefix) {
    if (!prefix) { this._cache.clear(); return; }
    for (const key of this._cache.keys()) {
      if (key.startsWith(prefix)) this._cache.delete(key);
    }
  }

  async cachedGet(path, ttl) {
    const cached = this._getCached(path);
    if (cached) return cached;
    const data = await this.get(path);
    this._setCache(path, data, ttl);
    return data;
  }

  async request(method, path, body = null, retried = false) {
    const headers = { 'Content-Type': 'application/json' };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const opts = { method, headers };
    if (body) opts.body = JSON.stringify(body);

    try {
      const res = await fetch(`${API_BASE}${path}`, opts);

      // Handle token expiry — auto refresh
      if (res.status === 401 && !retried) {
        const data = await res.json().catch(() => ({}));
        // Token de suplantación caducado/invalidado → volver a la sesión del SA
        if (this.isImpersonating()) {
          this.stopImpersonation();
          window.location.assign('/');
          throw new Error('La vista como otro usuario ha caducado');
        }
        if (data.code === 'TOKEN_EXPIRED' && this.refreshToken) {
          const refreshed = await this.doRefresh();
          if (refreshed) {
            return this.request(method, path, body, true);
          }
        }
        // Refresh failed or no refresh token — force logout
        this.clearAuth();
        window.dispatchEvent(new Event('auth:expired'));
        throw new Error('Sesión expirada');
      }

      const data = await res.json();

      if (!res.ok) {
        // Prueba terminada sin suscripción: un único aviso aunque fallen varias llamadas
        if (res.status === 402 && data.code === 'TRIAL_EXPIRED') {
          if (!this._trialToastAt || Date.now() - this._trialToastAt > 60000) {
            this._trialToastAt = Date.now();
            toast.warning('Periodo de prueba terminado', 'Activa tu suscripción en Configuración para seguir usando MedCover Pro.');
          }
          throw new Error(data.error);
        }
        // 429 rate limit — show countdown from Retry-After header
        if (res.status === 429) {
          const retryAfter = parseInt(res.headers.get('Retry-After') || data.retryAfter || 60);
          const mins = Math.ceil(retryAfter / 60);
          const msg = retryAfter < 60
            ? `Demasiadas solicitudes. Espera ${retryAfter} segundos.`
            : `Demasiadas solicitudes. Espera ${mins} minuto${mins === 1 ? '' : 's'}.`;
          toast.warning('Ralentiza', msg);
          throw new Error(msg);
        }
        // 503 maintenance — dispatch event + mostrar en todos los consumidores
        if (res.status === 503 && data?.code === 'MAINTENANCE_MODE') {
          window.dispatchEvent(new CustomEvent('platform:maintenance', {
            detail: { scope: data.scope, message: data.error },
          }));
          throw new Error(data.error || 'Plataforma en mantenimiento');
        }
        const errMsg = data.error || `Error ${res.status}`;
        // Show toast for user-facing errors (not for background polling)
        if (method !== 'GET') toast.error('Error', errMsg);
        // Adjunta details (e.g. validatePassword errors array) al Error para que
        // el caller pueda mostrar la lista específica de problemas.
        const apiErr = new Error(errMsg);
        if (data?.details) apiErr.details = data.details;
        if (data?.code) apiErr.code = data.code;
        apiErr.status = res.status;
        // Adjunta el body completo para que callers que necesiten diagnóstico
        // (ej: EmailTester) puedan acceder al payload del error.
        apiErr.body = data;
        throw apiErr;
      }

      // Show success toast for mutations
      if (method !== 'GET' && data.message) {
        toast.success('Hecho', data.message);
      }

      // Check if response came from cache (Service Worker)
      if (res.headers.get('X-From-Cache')) {
        data._fromCache = true;
        data._cacheDate = res.headers.get('X-Cache-Date');
      }

      return data;
    } catch (err) {
      // Network error — might be offline
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        toast.warning('Sin conexión', 'Los datos se mostrarán desde la caché.');
        throw new Error('Sin conexión. Los datos se mostrarán desde la caché.');
      }
      throw err;
    }
  }

  async doRefresh() {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });

        if (!res.ok) return false;

        const data = await res.json();
        this.setTokens(data.token, data.refreshToken);
        return true;
      } catch (err) {
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  get(path) { return this.request('GET', path); }

  // Abre un PDF/CSV de la API en otra pestaña con un token de descarga de 60 s
  // ligado a esa ruta (nunca el token de sesión en la URL). `path` es relativo a
  // la API, p. ej. '/admin/quotes/123/pdf' o '/admin/tax-reports/111?year=2026'.
  async openDownload(path) {
    const win = window.open('', '_blank'); // síncrono: evita el bloqueador de popups
    try {
      const pathname = path.split('?')[0];
      const { token } = await this.post('/auth/download-token', { path: pathname });
      const url = `${this.baseURL}${path}${path.includes('?') ? '&' : '?'}dl=${encodeURIComponent(token)}`;
      if (win) win.location.href = url; else window.location.assign(url);
    } catch (e) {
      if (win) win.close();
      toast.error('No se pudo descargar', e.message);
    }
  }

  saImpersonationTargets(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return this.get(`/superadmin/impersonation/targets${qs ? `?${qs}` : ''}`);
  }
  post(path, body) { this._cache.clear(); return this.request('POST', path, body); }
  put(path, body) { this._cache.clear(); return this.request('PUT', path, body); }
  delete(path, body) { this._cache.clear(); return this.request('DELETE', path, body); }

  // Auth
  async login(email, password) {
    const data = await this.post('/auth/login', { email, password });
    // 2FA flows: code+tmp_token (already enabled) o setup_token (admin obligado a configurarlo).
    // En ambos casos NO seteamos tokens — el caller (LoginPage) gestiona el siguiente paso.
    if (data.requires_2fa || data.requires_2fa_setup) return data;
    this.setTokens(data.token, data.refreshToken);
    return data;
  }
  async googleLogin(credential) {
    const data = await this.post('/auth/google-login', { credential });
    if (data.requires_2fa) return data;
    this.setTokens(data.token, data.refreshToken);
    return data;
  }
  me() { return this.get('/auth/me'); }
  updateProfile(data) { return this.put('/auth/profile', data); }
  changePassword(data) { return this.put('/auth/change-password', data); }
  async refresh() { return this.doRefresh(); }

  // 2FA
  twoFAStatus() { return this.get('/auth/2fa/status'); }
  twoFASetup() { return this.post('/auth/2fa/setup'); }
  twoFAVerifySetup(data) { return this.post('/auth/2fa/verify-setup', data); }
  twoFADisable(data) { return this.post('/auth/2fa/disable', data); }
  async twoFAValidate(tmp_token, code) {
    const data = await this.post('/auth/2fa/validate', { tmp_token, code });
    this.setTokens(data.token, data.refreshToken);
    return data;
  }
  // Forced 2FA setup desde el login (admin obligado pero sin 2FA aún)
  twoFAForcedSetup(setup_token) { return this.post('/auth/2fa/forced-setup', { setup_token }); }
  async twoFAForcedVerify(setup_token, secret, code) {
    const data = await this.post('/auth/2fa/forced-verify', { setup_token, secret, code });
    this.setTokens(data.token, data.refreshToken);
    return data;
  }

  // SuperAdmin
  saStats() { return this.get('/superadmin/stats'); }
  saTenants() { return this.get('/superadmin/tenants'); }
  saTenantMetrics(tenantId) { return this.get(`/superadmin/tenants/${tenantId}/metrics`); }
  // Directorio global de usuarios (SuperAdmin)
  saUsers(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return this.get(`/superadmin/users${qs ? `?${qs}` : ''}`);
  }
  saUserStatus(id, status) { return this.put(`/superadmin/users/${id}/status`, { status }); }
  saUserResetPassword(id) { return this.post(`/superadmin/users/${id}/reset-password`, {}); }
  saUserUnlock(id) { return this.post(`/superadmin/users/${id}/unlock`, {}); }
  saUserReset2FA(id) { return this.post(`/superadmin/users/${id}/reset-2fa`, {}); }
  saCreateSuperadmin(data) { return this.post('/superadmin/superadmins', data); }
  saExtendTrial(tenantId, days) { return this.post(`/superadmin/tenants/${tenantId}/trial`, { days }); }
  // Suscripción Stripe y cuota de IA de un tenant (SuperAdmin)
  saTenantBilling(id) { return this.get(`/superadmin/tenants/${id}/billing`); }
  saTenantBillingSync(id) { return this.post(`/superadmin/tenants/${id}/billing/sync`, {}); }
  saTenantBillingCancel(id, atPeriodEnd = true) { return this.post(`/superadmin/tenants/${id}/billing/cancel`, { at_period_end: atPeriodEnd }); }
  saTenantBillingResume(id) { return this.post(`/superadmin/tenants/${id}/billing/resume`, {}); }
  saTenantBillingPortal(id) { return this.post(`/superadmin/tenants/${id}/billing/portal`, {}); }
  saTenantBillingRefund(id, data) { return this.post(`/superadmin/tenants/${id}/billing/refund`, data); }
  saTenantAIQuota(id) { return this.get(`/superadmin/tenants/${id}/ai-quota`); }
  saTenantAIQuotaUpdate(id, data) { return this.put(`/superadmin/tenants/${id}/ai-quota`, data); }
  saTenantAICredit(id, amount_eur, note) { return this.post(`/superadmin/tenants/${id}/ai-credit`, { amount_eur, note }); }
  saAIUsage(from, to) {
    const p = new URLSearchParams();
    if (from) p.set('from', from); if (to) p.set('to', to);
    return this.get('/superadmin/ai-usage' + (p.toString() ? '?' + p.toString() : ''));
  }
  saHealth() { return this.get('/health/detailed'); }
  saSystemSettings() { return this.get('/superadmin/system-settings'); }
  saUpdateSystemSettings(settings) { return this.put('/superadmin/system-settings', { settings }); }
  saMaintenanceBulk(tenantIds, enabled, message) {
    return this.post('/superadmin/tenants/maintenance-bulk', { tenant_ids: tenantIds, enabled, message });
  }
  saHealthTest(service) { return this.post(`/superadmin/health-test/${service}`); }
  saAuditLog(params = {}) {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return this.get(`/superadmin/audit-log${qs ? `?${qs}` : ''}`);
  }
  saCreateTenant(data) { return this.post('/superadmin/tenants', data); }
  saUpdateTenant(id, data) { return this.put(`/superadmin/tenants/${id}`, data); }
  saApplyPlanModules(id) { return this.post(`/superadmin/tenants/${id}/apply-plan-modules`, {}); }
  saResetTenant(id, sections) { return this.post(`/superadmin/tenants/${id}/reset`, { sections }); }
  // Soft cancel — solo cambia status. Reversible reactivando.
  saCancelTenant(id) { return this.put(`/superadmin/tenants/${id}/cancel`, {}); }
  // HARD delete — borra todos los datos del tenant en cascada. Irreversible.
  // Requiere confirm code "ELIMINAR PERMANENTEMENTE" en el body.
  saDeleteTenant(id) { return this.delete(`/superadmin/tenants/${id}`, { confirm: 'ELIMINAR PERMANENTEMENTE' }); }
  saPlans() { return this.get('/superadmin/plans'); }
  saCreatePlan(data) { return this.post('/superadmin/plans', data); }
  saUpdatePlan(id, data) { return this.put(`/superadmin/plans/${id}`, data); }
  saDeletePlan(id) { return this.delete(`/superadmin/plans/${id}`); }
  saModules() { return this.get('/superadmin/modules'); }
  saCreateModule(data) { return this.post('/superadmin/modules', data); }
  saUpdateModule(id, data) { return this.put(`/superadmin/modules/${id}`, data); }
  saDeleteModule(id) { return this.delete(`/superadmin/modules/${id}`); }
  saProCategories() { return this.get('/superadmin/professional-categories'); }
  saCreateProCategory(data) { return this.post('/superadmin/professional-categories', data); }
  saUpdateProCategory(id, data) { return this.put(`/superadmin/professional-categories/${id}`, data); }
  saDeleteProCategory(id) { return this.delete(`/superadmin/professional-categories/${id}`); }
  saVehicleTypes() { return this.get('/superadmin/vehicle-types'); }
  saCreateVehicleType(data) { return this.post('/superadmin/vehicle-types', data); }
  saUpdateVehicleType(id, data) { return this.put(`/superadmin/vehicle-types/${id}`, data); }
  saDeleteVehicleType(id) { return this.delete(`/superadmin/vehicle-types/${id}`); }
  saResourceCrew() { return this.get('/superadmin/resource-crew'); }
  // Acepta array plano de {category_code, quantity, optional}; wrapper lo envía como {crew}
  // porque el backend espera req.body.crew. Fix del mismatch de signatures detectado en audit.
  saUpdateResourceCrew(code, crew) { return this.put(`/superadmin/resource-crew/${code}`, { crew }); }
  saTenantCategories(id) { return this.get(`/superadmin/tenants/${id}/categories`); }
  saUpdateTenantCategories(id, data) { return this.put(`/superadmin/tenants/${id}/categories`, data); }
  saTenantVehicleTypes(id) { return this.get(`/superadmin/tenants/${id}/vehicle-types`); }
  saUpdateTenantVehicleTypes(id, data) { return this.put(`/superadmin/tenants/${id}/vehicle-types`, data); }
  saCustomizeTenant(id, data) { return this.put(`/superadmin/tenants/${id}/customize`, data); }
  // Logo del tenant (data URL ≤ 200 KB tras resize cliente)
  saUploadTenantLogo(id, dataUrl) { return this.post(`/superadmin/tenants/${id}/logo`, { logo: dataUrl }); }
  saDeleteTenantLogo(id) { return this.delete(`/superadmin/tenants/${id}/logo`); }
  // Genera enlace de acceso (password_reset_token 7d) para el admin del tenant.
  // Si email_to_user=true, además envía el correo con el botón "Establecer mi contraseña".
  saGenerateTenantAccessLink(id, { user_id = null, email_to_user = false } = {}) {
    return this.post(`/superadmin/tenants/${id}/access-link`, { user_id, email_to_user });
  }
  // Billing / Suscripciones — dashboard SA
  saBillingOverview(months = 12) { return this.get(`/superadmin/billing/overview?months=${months}`); }
  saBillingPayments({ status = null, tenantId = null, limit = 50, offset = 0 } = {}) {
    const qs = new URLSearchParams({ limit, offset });
    if (status) qs.set('status', status);
    if (tenantId) qs.set('tenant_id', tenantId);
    return this.get(`/superadmin/billing/payments?${qs.toString()}`);
  }
  saBillingSuspend(id, reason) { return this.post(`/superadmin/tenants/${id}/billing/suspend`, { reason }); }
  saBillingReactivate(id) { return this.post(`/superadmin/tenants/${id}/billing/reactivate`, {}); }
  saBillingRecordPayment(id, payload) { return this.post(`/superadmin/tenants/${id}/billing/record-payment`, payload); }
  // Diagnóstico envío email (Resend)
  saTestEmail({ to, subject = null, message = null } = {}) { return this.post('/superadmin/email/test', { to, subject, message }); }
  saResendDomains() { return this.get('/superadmin/email/domains'); }

  // ─── Promo codes (SA) ───
  saPromos() { return this.get('/superadmin/promos'); }
  saCreatePromo(data) { return this.post('/superadmin/promos', data); }
  saUpdatePromo(id, data) { return this.put(`/superadmin/promos/${id}`, data); }
  saDeletePromo(id) { return this.delete(`/superadmin/promos/${id}`); }

  // Plan custom builder — calcula precio sugerido para módulos elegidos
  saCalculateCustomPlan({ module_slugs, users = 1, margin_pct = 100 }) {
    return this.post('/superadmin/plans/calculate-custom', { module_slugs, users, margin_pct });
  }

  // ─── Público (sin auth) ───
  publicPlans() { return this.get('/auth/public-plans'); }
  validatePromo({ code, plan_id = null }) { return this.post('/auth/validate-promo', { code, plan_id }); }

  // Checkout extendido: acepta plan_id, promo_code, accept_terms (commitment), seats
  billingCheckoutExtended({ plan_id, promo_code = null, accept_terms = false, seats = null }) {
    return this.post('/billing/checkout', { plan_id, promo_code, accept_terms, seats });
  }
  billingCancelRequest() { return this.post('/billing/cancel-request', {}); }
  billingCancelUndo() { return this.post('/billing/cancel-undo', {}); }
  // Reportar error desde ErrorBoundary o cualquier parte de la app — público
  // (acepta auth opcional). Crea ticket urgente + email instantáneo al SA.
  reportError({ message, stack = null, page = null, url = null, user_agent = null, extra = null } = {}) {
    return this.post('/auth/error-report', { message, stack, page, url, user_agent, extra });
  }
  // Datos de prueba — solo SA puede actuar sobre la BD del tenant
  saLoadTestData(id) { return this.post(`/superadmin/tenants/${id}/test-data/load`, {}); }
  saRemoveTestData(id, sections, confirm_code) { return this.post(`/superadmin/tenants/${id}/test-data/remove`, { sections, confirm_code }); }
  saTestSimulate(id, scenario) { return this.post(`/superadmin/tenants/${id}/test-simulate`, { scenario }); }
  // SuperAdmin tickets
  saTickets() { return this.get('/superadmin/tickets'); }
  saTicket(id) { return this.get(`/superadmin/tickets/${id}`); }
  saTicketReply(id, data) { return this.post(`/superadmin/tickets/${id}/reply`, data); }
  saTicketStatus(id, data) { return this.put(`/superadmin/tickets/${id}/status`, data); }
  // Admin tickets
  adminTickets() { return this.get('/admin/tickets'); }
  adminCreateTicket(data) { return this.post('/admin/tickets', data); }
  adminTicket(id) { return this.get(`/admin/tickets/${id}`); }
  adminTicketReply(id, data) { return this.post(`/admin/tickets/${id}/reply`, data); }

  // Admin
  adminDashboard() { return this.cachedGet('/admin/dashboard', 15000); }
  adminRequests(status) { return this.cachedGet(`/admin/requests${status ? `?status=${status}` : ''}`); }
  adminRequest(id) { return this.get(`/admin/requests/${id}`); }
  adminUpdateRequest(id, data) { this.invalidateCache('/admin/'); return this.put(`/admin/requests/${id}`, data); }
  adminUpdateRequestResources(id, resources) { this.invalidateCache('/admin/'); return this.put(`/admin/requests/${id}/resources`, { resources }); }
  adminValidatePayment(id) { this.invalidateCache('/admin/'); return this.put(`/admin/requests/${id}/validate-payment`); }
  adminSearch(q) { return this.get(`/admin/search?q=${encodeURIComponent(q)}`); }
  adminReporting() { return this.cachedGet('/admin/reporting', 60000); }
  // adminTestSimulate ELIMINADO — solo SuperAdmin puede simular (saTestSimulate más abajo)

  // Preload all key data for a role — call after login
  // `user` para no precargar secciones de módulos que la empresa no tiene (403)
  async preloadAdmin(user) {
    const mods = Array.isArray(user?.enabled_modules) ? user.enabled_modules : [];
    try {
      await Promise.all([
        this.adminDashboard(),
        this.adminRequests(),
        this.adminClients(),
        this.adminProfessionals(),
        mods.includes('billing_adv') ? this.adminInvoices() : null,
        this.adminResourceTypes(),
      ]);
    } catch(e) { console.warn('Preload partial:', e.message); }
  }

  async preloadClient() {
    try {
      await Promise.all([
        this.clientDashboard(),
        this.clientRequests(),
        this.clientQuotes(),
        this.clientInvoices(),
        this.get('/client/resource-types').then(d => this._setCache('/client/resource-types', d, 60000)),
      ]);
    } catch(e) { console.warn('Preload partial:', e.message); }
  }
  adminExport(type) {
    // this.token (no this.storage.get) — la storage helper era un patrón antiguo.
    // Sin esto el CSV export rompía con "Cannot read properties of undefined".
    return this.openDownload(`/admin/export/${type}`);
  }
  adminClients() { return this.cachedGet('/admin/clients'); }
  adminCreateClient(data) { this.invalidateCache('/admin/'); return this.post('/admin/clients', data); }
  adminUpdateClient(id, data) { this.invalidateCache('/admin/'); return this.put(`/admin/clients/${id}`, data); }
  adminResendClientCredentials(id) { return this.post(`/admin/clients/${id}/resend-credentials`, {}); }
  adminPendingClients() { return this.get('/admin/clients/pending'); }
  adminApproveClient(id) { return this.post(`/admin/clients/${id}/approve`, {}); }
  adminRejectClient(id, reason) { return this.post(`/admin/clients/${id}/reject`, { reason }); }
  adminProfessionals() { return this.cachedGet('/admin/professionals'); }
  adminProfessional(id) { return this.get(`/admin/professionals/${id}`); }
  adminCreateProfessional(data) { return this.post('/admin/professionals', data); }
  adminUpdateProfessional(id, data) { return this.put(`/admin/professionals/${id}`, data); }
  adminDeleteProfessional(id) { return this.delete(`/admin/professionals/${id}`); }
  adminApproveProfessional(id) { return this.put(`/admin/professionals/${id}/approve`); }
  adminRejectProfessional(id, data) { return this.put(`/admin/professionals/${id}/reject`, data); }
  adminRequestDocs(id, data) { return this.put(`/admin/professionals/${id}/request-docs`, data); }
  adminResourceTypes() { return this.cachedGet('/admin/resource-types', 60000); }
  adminCreateResourceType(data) { return this.post('/admin/resource-types', data); }
  adminUpdateResourceType(id, data) { return this.put(`/admin/resource-types/${id}`, data); }
  adminRatesOverview() { return this.cachedGet('/admin/rates-overview', 60000); }
  adminCreateQuote(data) { return this.post('/admin/quotes', data); }
  adminGenerateQuote(requestId, data) { this.invalidateCache('/admin/'); return this.post(`/admin/quotes/generate/${requestId}`, data || {}); }
  adminSendQuote(id) { return this.put(`/admin/quotes/${id}/send`); }
  adminAnnulQuote(id) { return this.put(`/admin/quotes/${id}/annul`); }
  adminQuotePDF(id) { return `${this.baseURL}/admin/quotes/${id}/pdf`; }
  // Abre el PDF en una pestaña nueva — el endpoint acepta token en query.
  adminOpenQuotePdf(id) { return this.openDownload(`/admin/quotes/${id}/pdf`); }
  adminQuotes() { return this.cachedGet('/admin/quotes'); }
  adminUpdateTerms(data) { return this.put('/admin/terms', data); }
  adminCreateAssignment(data) { return this.post('/admin/assignments', data); }
  adminInvoices(status) { return this.cachedGet(`/admin/invoices${status ? `?status=${status}` : ''}`); }
  adminCreateInvoice(data) { this.invalidateCache('/admin/'); return this.post('/admin/invoices', data); }
  adminUpdateInvoice(id, data) { this.invalidateCache('/admin/'); return this.put(`/admin/invoices/${id}`, data); }
  adminInvoicePdf(id) { return this.openDownload(`/admin/invoices/${id}/pdf`); }
  adminSettlements() { return this.cachedGet('/admin/settlements'); }
  adminSettlementStatus(id, status) { this.invalidateCache('/admin/settlements'); return this.put(`/admin/settlements/${id}/status`, { status }); }
  adminVehicles() { return this.cachedGet('/admin/vehicles'); }
  adminVehicleTypes() { return this.cachedGet('/admin/vehicle-types', 60000); }
  adminUpdateVehicleTypeChecklist(id, checklist) { return this.put(`/admin/vehicle-types/${id}/checklist`, { equipment_checklist: checklist }); }
  adminBases() { return this.cachedGet('/admin/bases', 30000); }
  adminCreateBase(data) { return this.post('/admin/bases', data); }
  adminUpdateBase(id, data) { return this.put(`/admin/bases/${id}`, data); }
  adminDeleteBase(id) { return this.delete(`/admin/bases/${id}`); }
  adminAvailableVehicles(params) { return this.get(`/admin/available-vehicles?${new URLSearchParams(params)}`); }
  adminAvailabilityCheck(requestId) { return this.get(`/admin/availability-check/${requestId}`); }
  adminCreateVehicle(data) { return this.post('/admin/vehicles', data); }
  adminUpdateVehicle(id, data) { return this.put(`/admin/vehicles/${id}`, data); }
  adminDeleteVehicle(id) { return this.delete(`/admin/vehicles/${id}`); }
  adminVehicleSessions(id) { return this.get(`/admin/vehicles/${id}/sessions`); }
  adminSessions() { return this.get('/admin/sessions'); }
  adminSession(id) { return this.get(`/admin/sessions/${id}`); }
  adminCreateSession(data) { return this.post('/admin/sessions', data); }
  adminUpdateSession(id, data) { return this.put(`/admin/sessions/${id}`, data); }
  adminDeleteSession(id) { return this.delete(`/admin/sessions/${id}`); }
  adminSessionPatients(id) { return this.get(`/admin/sessions/${id}/patients`); }
  adminAddSessionPatient(id, data) { return this.post(`/admin/sessions/${id}/patient`, data); }
  adminCreateVehicleSession(data) { return this.post('/admin/vehicle-sessions', data); }
  adminLiveTracking() { return this.get('/admin/live-tracking'); }
  // Fleet maintenance (B1)
  adminMaintenance(filters = {}) {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return this.get('/admin/maintenance' + (q ? '?' + q : ''));
  }
  adminMaintenanceAlerts() { return this.get('/admin/maintenance/alerts'); }
  adminMaintenanceFleetSummary() { return this.get('/admin/maintenance/summary/fleet'); }
  adminMaintenanceAlertConfig() { return this.get('/admin/maintenance/alert-config'); }
  adminMaintenanceAlertConfigUpdate(data) { return this.put('/admin/maintenance/alert-config', data); }
  adminMaintenanceGet(id) { return this.get(`/admin/maintenance/${id}`); }
  adminMaintenanceCreate(data) { return this.post('/admin/maintenance', data); }
  adminMaintenanceUpdate(id, data) { return this.put(`/admin/maintenance/${id}`, data); }
  adminMaintenanceDelete(id) { return this.delete(`/admin/maintenance/${id}`); }
  // Shifts + time tracking (B3 — RD 8/2019)
  adminShifts(from, to, userId) {
    const p = new URLSearchParams();
    if (from) p.set('from', from); if (to) p.set('to', to); if (userId) p.set('user_id', userId);
    return this.get('/admin/shifts' + (p.toString() ? '?' + p.toString() : ''));
  }
  adminShiftCreate(data) { return this.post('/admin/shifts', data); }
  adminShiftUpdate(id, data) { return this.put(`/admin/shifts/${id}`, data); }
  adminShiftDelete(id) { return this.delete(`/admin/shifts/${id}`); }
  adminTimeEntries(from, to, userId) {
    const p = new URLSearchParams();
    if (from) p.set('from', from); if (to) p.set('to', to); if (userId) p.set('user_id', userId);
    return this.get('/admin/time-entries' + (p.toString() ? '?' + p.toString() : ''));
  }
  adminTimeReport(userId, year, month) {
    return this.get(`/admin/time-entries/report?user_id=${userId}&year=${year}&month=${month}`);
  }
  // Retribución a profesionales (facturas autónomos + cálculo horas × hourly_rate)
  adminRetributionPreview(professionalId, year, month) {
    return this.get(`/admin/retribution/preview?professional_id=${professionalId}&year=${year}&month=${month}`);
  }
  adminProInvoices(filters = {}) {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    return this.get('/admin/professional-invoices' + (p.toString() ? '?' + p.toString() : ''));
  }
  adminProInvoiceCreate(data) { return this.post('/admin/professional-invoices', data); }
  adminProInvoiceUpdate(id, data) { return this.put(`/admin/professional-invoices/${id}`, data); }
  // Modelo 111 / 190 — retenciones IRPF para gestor
  adminTax111(year, quarter) { return this.get(`/admin/tax-reports/111?year=${year}&quarter=${quarter}`); }
  adminTax190(year) { return this.get(`/admin/tax-reports/190?year=${year}`); }
  // Hospitales — CRUD + portal
  adminHospitals() { return this.get('/admin/hospitals'); }
  adminHospitalGet(id) { return this.get(`/admin/hospitals/${id}`); }
  adminHospitalCreate(data) { return this.post('/admin/hospitals', data); }
  adminHospitalUpdate(id, data) { return this.put(`/admin/hospitals/${id}`, data); }
  adminHospitalDelete(id) { return this.delete(`/admin/hospitals/${id}`); }
  adminHospitalInviteUser(id, data) { return this.post(`/admin/hospitals/${id}/invite-user`, data); }
  adminHospitalRemoveUser(hospitalId, userId) { return this.delete(`/admin/hospitals/${hospitalId}/users/${userId}`); }
  // Portal del hospital
  hospitalProfile() { return this.get('/hospital/profile'); }
  hospitalDashboard() { return this.get('/hospital/dashboard'); }
  hospitalPatients(filters = {}) {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    return this.get('/hospital/patients' + (p.toString() ? '?' + p.toString() : ''));
  }
  hospitalPatient(id) { return this.get(`/hospital/patients/${id}`); }
  hospitalCreateServiceRequest(data) { return this.post('/hospital/service-request', data); }
  hospitalServiceRequests() { return this.get('/hospital/service-requests'); }
  // Roles personalizados (perfiles de permisos por tenant)
  adminCustomRoles() { return this.get('/admin/custom-roles'); }
  adminCustomRoleGet(id) { return this.get(`/admin/custom-roles/${id}`); }
  adminCustomRoleCreate(data) { return this.post('/admin/custom-roles', data); }
  adminCustomRoleUpdate(id, data) { return this.put(`/admin/custom-roles/${id}`, data); }
  adminCustomRoleDelete(id) { return this.delete(`/admin/custom-roles/${id}`); }
  adminAssignUserCustomRole(userId, roleId) { return this.put(`/admin/users/${userId}/custom-role`, { custom_role_id: roleId }); }
  // Centralita de despacho (admin)
  adminDispatch(filters = {}) {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    return this.get('/admin/dispatch' + (p.toString() ? '?' + p.toString() : ''));
  }
  adminDispatchGet(id) { return this.get(`/admin/dispatch/${id}`); }
  adminDispatchCreate(data) { return this.post('/admin/dispatch', data); }
  adminDispatchAssign(id, data) { return this.post(`/admin/dispatch/${id}/assign`, data); }
  adminDispatchState(id, state) { return this.post(`/admin/dispatch/${id}/state`, { state }); }
  // Centralita (recurso/ambulancia)
  resourceDispatchActive() { return this.get('/resource/dispatch/active'); }
  resourceDispatchState(id, state) { return this.post(`/resource/dispatch/${id}/state`, { state }); }
  resourceGps(lat, lng) { return this.post('/resource/gps', { lat, lng }); }
  // Cuota IA + packs + auto-recarga
  adminAIQuota() { return this.get('/admin/ai-quota'); }
  adminAIPacks() { return this.get('/admin/ai-quota/packs'); }
  adminAICheckout(pack) { return this.post('/admin/ai-quota/checkout', { pack }); }
  adminAIHistory() { return this.get('/admin/ai-quota/history'); }
  adminAIPurchases() { return this.get('/admin/ai-quota/purchases'); }
  adminAIAutorefill(data) { return this.put('/admin/ai-quota/autorefill', data); }
  // Pólizas de seguros (RC + flota + accidentes)
  adminInsurancePolicies(filters = {}) {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    return this.get('/admin/insurance-policies' + (p.toString() ? '?' + p.toString() : ''));
  }
  adminInsuranceAlerts() { return this.get('/admin/insurance-policies/alerts'); }
  adminInsuranceCreate(data) { return this.post('/admin/insurance-policies', data); }
  adminInsuranceUpdate(id, data) { return this.put(`/admin/insurance-policies/${id}`, data); }
  adminInsuranceDelete(id) { return this.delete(`/admin/insurance-policies/${id}`); }
  // AEAT Certificate (VeriFactu envío inmediato)
  adminAeatCertGet() { return this.get('/admin/aeat-certificate'); }
  adminAeatCertUpload(file_data, password) { return this.post('/admin/aeat-certificate', { file_data, password }); }
  adminAeatCertDelete() { return this.delete('/admin/aeat-certificate'); }
  // Professional self-clock
  proClockIn(type, lat, lng) { return this.post('/professional/clock', { type, lat, lng }); }
  proClockToday() { return this.get('/professional/clock/today'); }
  proClockHistory(from, to) {
    const p = new URLSearchParams();
    if (from) p.set('from', from); if (to) p.set('to', to);
    return this.get('/professional/clock/history' + (p.toString() ? '?' + p.toString() : ''));
  }
  // Incidents — cualquier rol autenticado reporta a quality_incidents
  reportIncident(data) { return this.post('/incidents/report', data); }
  // Medical stock (B4)
  adminStock(filters = {}) {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return this.get('/admin/stock' + (q ? '?' + q : ''));
  }
  adminStockAlerts() { return this.get('/admin/stock/alerts'); }
  adminStockCreate(data) { return this.post('/admin/stock', data); }
  adminStockUpdate(id, data) { return this.put(`/admin/stock/${id}`, data); }
  adminStockDelete(id) { return this.delete(`/admin/stock/${id}`); }
  adminStockAdjust(id, delta) { return this.post(`/admin/stock/${id}/adjust`, { delta }); }
  // Patient records (B2 — PCR global view)
  adminPatientRecords(filters = {}) {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v !== '' && v != null)).toString();
    return this.get('/admin/patient-records' + (q ? '?' + q : ''));
  }
  // BI executive dashboard (B6)
  adminBIExecutive() { return this.get('/admin/bi/executive'); }
  // Professional certifications (B7)
  adminCertifications(filters = {}) {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return this.get('/admin/certifications' + (q ? '?' + q : ''));
  }
  adminCertificationsAlerts() { return this.get('/admin/certifications/alerts'); }
  adminCertificationCreate(data) { return this.post('/admin/certifications', data); }
  adminCertificationUpdate(id, data) { return this.put(`/admin/certifications/${id}`, data); }
  adminCertificationVerify(id, verified) { return this.put(`/admin/certifications/${id}/verify`, { verified }); }
  adminCertificationDelete(id) { return this.delete(`/admin/certifications/${id}`); }
  // CRM Leads pipeline (B8)
  adminLeads(filters = {}) {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return this.get('/admin/leads' + (q ? '?' + q : ''));
  }
  adminLeadsSummary() { return this.get('/admin/leads/summary'); }
  adminLeadCreate(data) { return this.post('/admin/leads', data); }
  adminLeadUpdate(id, data) { return this.put(`/admin/leads/${id}`, data); }
  adminLeadConvert(id) { return this.post(`/admin/leads/${id}/convert`); }
  adminLeadDelete(id) { return this.delete(`/admin/leads/${id}`); }
  // Quality incidents (B9 — ISO 9001)
  adminQualityIncidents(filters = {}) {
    const q = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    return this.get('/admin/quality-incidents' + (q ? '?' + q : ''));
  }
  adminQualityPareto() { return this.get('/admin/quality-incidents/pareto'); }
  adminQualityCreate(data) { return this.post('/admin/quality-incidents', data); }
  adminQualityUpdate(id, data) { return this.put(`/admin/quality-incidents/${id}`, data); }
  adminQualityDelete(id) { return this.delete(`/admin/quality-incidents/${id}`); }
  adminCalendar(month, year) { return this.get(`/admin/calendar?month=${month}&year=${year}`); }
  adminAvailableForSlot(params) { return this.get(`/admin/available-for-slot?${new URLSearchParams(params)}`); }
  adminProfessionalHours() { return this.cachedGet('/admin/professional-hours', 15000); }
  adminAvailability(start, end) { return this.get(`/admin/availability?start_date=${start||''}&end_date=${end||''}`); }
  adminSetDifficulty(id, difficulty) { return this.put(`/admin/requests/${id}/difficulty`, { difficulty }); }
  adminSetProScore(id, score) { return this.put(`/admin/professionals/${id}/score`, { score }); }
  adminReassign(id, data) { return this.put(`/admin/assignments/${id}/reassign`, data); }
  adminAutoAssign(data) { return this.post('/admin/auto-assign', data); }
  adminSettings() { return this.get('/admin/settings'); }
  adminUpdateSettings(data) { return this.put('/admin/settings', data); }
  // Logo (branding) del propio tenant
  adminUploadLogo(dataUrl) { return this.post('/admin/settings/logo', { logo: dataUrl }); }
  // Modo demo — admin del tenant gestiona datos de demostración
  adminDemoStatus()  { return this.get('/admin/demo/status'); }
  adminDemoEnable()  { return this.post('/admin/demo/enable', {}); }
  adminDemoDisable() { return this.post('/admin/demo/disable', {}); }
  adminDeleteLogo() { return this.delete('/admin/settings/logo'); }
  // adminLoadTestData / adminRemoveTestData ELIMINADOS — solo SuperAdmin (saLoad/Remove/Simulate)
  // Documents (Enterprise)
  adminDocTypes() { return this.get('/admin/documents/types'); }
  adminDocUpload(data) { return this.post('/admin/documents/upload', data); }
  adminDocList(proId) { return this.get(`/admin/documents/${proId}`); }
  adminDocDownload(proId, docType) { return this.get(`/admin/documents/${proId}/${docType}`); }
  adminDocVerify(proId, docType, data) { return this.put(`/admin/documents/${proId}/${docType}/verify`, data); }
  // Checklists
  adminChecklists() { return this.get('/admin/checklists'); }
  adminVehicleChecklist(vehicleId) { return this.get(`/admin/vehicles/${vehicleId}/checklist`); }
  adminUpdateVehicleChecklist(vehicleId, items) { this.invalidateCache('/admin/'); return this.put(`/admin/vehicles/${vehicleId}/checklist`, { items }); }
  adminUpdateChecklist(vtId, items) { return this.put(`/admin/checklists/${vtId}`, { items }); }
  adminChecklistIncidents() { return this.get('/admin/checklists/incidents'); }
  adminResolveIncident(id) { return this.put(`/admin/checklists/reports/${id}/resolve`, {}); }
  // Password management
  adminResetPassword(userId, pw) { return this.put(`/admin/users/${userId}/reset-password`, { new_password: pw }); }
  forgotPassword(email) { return this.post('/auth/forgot-password', { email }); }
  resetPassword(token, pw) { return this.post('/auth/reset-password', { token, new_password: pw }); }
  adminClientRates(clientId) { return this.get(`/admin/client-rates/${clientId}`); }
  adminUpdateClientRates(clientId, data) { return this.put(`/admin/client-rates/${clientId}`, data); }
  adminAIAutoAssign(data) { return this.post('/admin/ai-auto-assign', data); }
  adminAIInsights() { return this.post('/admin/ai/insights'); }

  // Users management (admin)
  adminUsers(params = {}) {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return this.get(`/admin/users${qs ? `?${qs}` : ''}`);
  }
  adminUpdateUser(id, data) { return this.put(`/admin/users/${id}`, data); }
  adminSuspendUser(id) { return this.put(`/admin/users/${id}/suspend`); }
  adminActivateUser(id) { return this.put(`/admin/users/${id}/activate`); }
  adminDeleteUser(id) { return this.delete(`/admin/users/${id}`); }
  adminResetUserPassword(id, new_password) { return this.put(`/admin/users/${id}/reset-password`, { new_password }); }

  // Invitations
  adminInvitations() { return this.get('/admin/invitations'); }
  adminCreateInvitation(data) { return this.post('/admin/invitations', data); }
  adminDeleteInvitation(id) { return this.delete(`/admin/invitations/${id}`); }
  adminResendInvitation(id) { return this.post(`/admin/invitations/${id}/resend`); }
  getInvitation(token) { return this.get(`/auth/invitation/${token}`); }
  async acceptInvitation(data) {
    const res = await this.post('/auth/accept-invitation', data);
    this.setTokens(res.token, res.refreshToken);
    return res;
  }

  // Auth - public registration
  registerProfessional(data) { return this.post('/auth/register-professional', data); }
  checkSlug(slug) { return this.get(`/auth/check-slug/${encodeURIComponent(slug)}`); }
  async signupTenant(data) {
    const res = await this.post('/auth/signup-tenant', data);
    this.setTokens(res.token, res.refreshToken);
    return res;
  }

  // Client
  clientDashboard() { return this.cachedGet('/client/dashboard', 15000); }
  clientRequests() { return this.cachedGet('/client/requests'); }
  clientCreateRequest(data) { return this.post('/client/requests', data); }
  clientQuotes() { return this.cachedGet('/client/quotes'); }
  clientSignQuote(id, data) { return this.put(`/client/quotes/${id}/sign`, data); }
  clientRejectQuote(id) { return this.put(`/client/quotes/${id}/reject`); }
  clientArchiveRequest(id) { return this.put(`/client/requests/${id}/archive`); }
  clientDeleteRequest(id, reason) { this._cache.clear(); return this.request('DELETE', `/client/requests/${id}`, reason ? { reason } : null); }
  clientInvoices() { return this.cachedGet('/client/invoices'); }
  clientResourceTypes() { return this.get('/client/resource-types'); }

  // Professional
  proServices() { return this.get('/professional/services'); }
  proAccept(id) { return this.put(`/professional/services/${id}/accept`); }
  proReject(id) { return this.put(`/professional/services/${id}/reject`); }
  proDashboard() { return this.get('/professional/dashboard'); }
  proProfile() { return this.get('/professional/profile'); }
  proUpdateProfile(data) { return this.put('/professional/profile', data); }
  proUploadDocument(data) { return this.post('/professional/documents', data); }
  proSettlements() { return this.get('/professional/settlements'); }
  proAvailability(month, year) { return this.get(`/professional/availability?month=${month||''}&year=${year||''}`); }
  proAddAvailability(data) { return this.post('/professional/availability', data); }
  proDeleteAvailability(id) { return this.delete(`/professional/availability/${id}`); }

  // Billing (Stripe)
  billingSubscription() { return this.get('/billing/subscription'); }
  billingCheckout(plan_id) { return this.post('/billing/checkout', { plan_id }); }
  billingPortal() { return this.post('/billing/portal'); }

  // WhatsApp
  waStatus() { return this.get('/whatsapp/status'); }
  waMessages() { return this.get('/whatsapp/messages'); }
  waSend(data) { return this.post('/whatsapp/send', data); }
  waSendText(data) { return this.post('/whatsapp/send-text', data); }

  // Google Calendar
  calStatus() { return this.get('/calendar/status'); }
  calAuth() { return this.get('/calendar/auth'); }
  calSync(requestId) { return this.post(`/calendar/sync/${requestId}`); }
  calDisconnect() { return this.delete('/calendar/disconnect'); }

  // Chat
  chatConversations() { return this.get('/chat/conversations'); }
  chatCreateConversation(data) { return this.post('/chat/conversations', data); }
  chatMessages(convId) { return this.get(`/chat/conversations/${convId}/messages`); }
  chatSendMessage(convId, body) { return this.post(`/chat/conversations/${convId}/messages`, { body }); }
  chatUnread() { return this.get('/chat/unread'); }
  chatUsers() { return this.get('/chat/users'); }

  // Shared
  notifications() { return this.get('/notifications'); }
  readAllNotifs() { return this.put('/notifications/read-all'); }
  readNotif(id) { return this.put(`/notifications/${id}/read`); }

  // Resource / Ambulance
  resVehicle() { return this.get('/resource/vehicle'); }
  resActiveSession() { return this.get('/resource/session/active'); }
  resSessions() { return this.get('/resource/sessions'); }
  resCrewSign(sessionId, data) { return this.post(`/resource/session/${sessionId}/crew-sign`, data); }
  resChecklist(sessionId, data) { return this.put(`/resource/session/${sessionId}/checklist`, data); }
  resAdvancePhase(sessionId, data) { return this.post(`/resource/session/${sessionId}/phase`, data); }
  resUpdateGPS(sessionId, lat, lng) { return this.post(`/resource/session/${sessionId}/gps`, { lat, lng }); }
  resActivateSession(sessionId) { return this.post(`/resource/session/${sessionId}/activate`); }
  resLogIncident(sessionId, data) { return this.post(`/resource/session/${sessionId}/incident`, data); }
  resResponsibleSign(sessionId, data) { return this.put(`/resource/session/${sessionId}/responsible-sign`, data); }
  resUpdateNotes(sessionId, data) { return this.put(`/resource/session/${sessionId}/notes`, data); }
  resSummary(sessionId) { return this.get(`/resource/session/${sessionId}/summary`); }
  resAvailableCrew(sessionId) { return this.get(`/resource/session/${sessionId}/available-crew`); }
  resPatients(sessionId) { return this.get(`/resource/session/${sessionId}/patients`); }
  resAddPatient(sessionId, data) { return this.post(`/resource/session/${sessionId}/patient`, data); }
  resUpdatePatient(id, data) { return this.put(`/resource/patient/${id}`, data); }
  resDeletePatient(id) { return this.delete(`/resource/patient/${id}`); }
  resHospitals() { return this.get('/resource/hospitals'); }
}

export const api = new ApiClient();
export default api;

// ═══════════════════════════════════════════════
// SERVICE WORKER REGISTRATION
// ═══════════════════════════════════════════════
export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then(reg => {
          console.log('SW registered:', reg.scope);
          // Request background sync permission
          if ('SyncManager' in window) {
            reg.sync.register('medcover-sync').catch(() => {});
          }
        })
        .catch(err => console.log('SW registration failed:', err));
    });
  }
}
